import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "ar" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const translations: Record<string, Record<Language, string>> = {
  // Navigation
  "nav.home": { ar: "الرئيسية", en: "Home" },
  "nav.about": { ar: "من نحن", en: "About Us" },
  "nav.services": { ar: "خدماتنا", en: "Our Services" },
  "nav.aluminum": { ar: "الألمنيوم", en: "Aluminum" },
  "nav.gallery": { ar: "معرض الصور", en: "Gallery" },
  "nav.articles": { ar: "مقالات", en: "Articles" },
  "nav.kitchen": { ar: "خزائن المطبخ", en: "Kitchen Cabinets" },
  "nav.contact": { ar: "تواصل معنا", en: "Contact Us" },

  // Hero Section
  "hero.title": { ar: "مجموعة الفنون للصناعات الحديدية", en: "ALFUNOON Group for Iron Industries" },
  "hero.subtitle": { ar: "نحول أفكاركم إلى واقع معدني", en: "Transforming Your Ideas into Metal Reality" },
  "hero.description": { ar: "خبرة تمتد لأكثر من 10 سنوات في تصنيع وتركيب جميع أنواع المعادن", en: "Over 10 years of experience in manufacturing and installing all types of metals" },
  "hero.cta": { ar: "تواصل معنا", en: "Contact Us" },
  "hero.gallery": { ar: "معرض الأعمال", en: "Our Gallery" },

  // About Section
  "about.title": { ar: "من نحن", en: "About Us" },
  "about.subtitle": { ar: "مجموعة الفنون للصناعات الحديدية", en: "ALFUNOON Group for Iron Industries" },
  "about.description": { ar: "نحن شركة رائدة في مجال تصنيع وتركيب الحديد والألمنيوم والزجاج والرخام والستانلس ستيل. نقدم حلولاً متكاملة للمشاريع السكنية والتجارية والصناعية بأعلى معايير الجودة.", en: "We are a leading company in manufacturing and installing iron, aluminum, glass, marble, and stainless steel. We provide integrated solutions for residential, commercial, and industrial projects with the highest quality standards." },
  "about.experience": { ar: "سنوات خبرة", en: "Years Experience" },
  "about.projects": { ar: "مشروع منجز", en: "Completed Projects" },
  "about.clients": { ar: "عميل سعيد", en: "Happy Clients" },
  "about.awards": { ar: "جائزة تميز", en: "Excellence Awards" },

  // Services Section
  "services.title": { ar: "خدماتنا", en: "Our Services" },
  "services.subtitle": { ar: "نقدم مجموعة متكاملة من الخدمات المتخصصة", en: "We offer a comprehensive range of specialized services" },
  "services.iron": { ar: "أعمال الحديد", en: "Iron Works" },
  "services.iron.desc": { ar: "تصنيع وتركيب جميع أنواع الأبواب والنوافذ والسلالم الحديدية", en: "Manufacturing and installing all types of iron doors, windows, and stairs" },
  "services.aluminum": { ar: "أعمال الألمنيوم", en: "Aluminum Works" },
  "services.aluminum.desc": { ar: "واجهات ألمنيوم وشبابيك وأبواب بتصاميم عصرية", en: "Aluminum facades, windows, and doors with modern designs" },
  "services.glass": { ar: "أعمال الزجاج", en: "Glass Works" },
  "services.glass.desc": { ar: "زجاج معماري وديكوري بجميع الأنواع والمقاسات", en: "Architectural and decorative glass of all types and sizes" },
  "services.marble": { ar: "أعمال الرخام", en: "Marble Works" },
  "services.marble.desc": { ar: "تركيب وتلميع الرخام للأرضيات والجدران", en: "Marble installation and polishing for floors and walls" },
  "services.stainless": { ar: "ستانلس ستيل", en: "Stainless Steel" },
  "services.stainless.desc": { ar: "تصنيع مطابخ ودرابزين ومعدات من الستانلس ستيل", en: "Manufacturing kitchens, railings, and stainless steel equipment" },
  "services.wood": { ar: "أعمال الخشب", en: "Wood Works" },
  "services.wood.desc": { ar: "تصنيع أثاث وأبواب خشبية بتصاميم فريدة", en: "Manufacturing furniture and wooden doors with unique designs" },

  // Contact Section
  "contact.title": { ar: "تواصل معنا", en: "Contact Us" },
  "contact.subtitle": { ar: "نحن هنا لمساعدتك", en: "We are here to help you" },
  "contact.name": { ar: "الاسم", en: "Name" },
  "contact.email": { ar: "البريد الإلكتروني", en: "Email" },
  "contact.phone": { ar: "رقم الهاتف", en: "Phone Number" },
  "contact.message": { ar: "الرسالة", en: "Message" },
  "contact.send": { ar: "إرسال", en: "Send" },
  "contact.address": { ar: "العنوان", en: "Address" },
  "contact.workingHours": { ar: "ساعات العمل", en: "Working Hours" },

  // Footer
  "footer.rights": { ar: "جميع الحقوق محفوظة", en: "All Rights Reserved" },
  "footer.quickLinks": { ar: "روابط سريعة", en: "Quick Links" },
  "footer.followUs": { ar: "تابعنا", en: "Follow Us" },

  // Common
  "common.readMore": { ar: "اقرأ المزيد", en: "Read More" },
  "common.viewAll": { ar: "عرض الكل", en: "View All" },
  "common.loading": { ar: "جاري التحميل...", en: "Loading..." },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem("language");
    return (savedLanguage as Language) || "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (key: string): string => {
    return translations[key]?.[language] || key;
  };

  const isRTL = language === "ar";

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
